'use client';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Check, CheckCircle2, Loader2, Sparkles, Users } from 'lucide-react';
import { useState } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import Navbar from '../components/Navbar/Navbar';

const ROLES = [
    { id: 'social', en: 'Social Media & Content', ne: 'सामाजिक सञ्जाल र सामग्री' },
    { id: 'tech', en: 'Tech & Data', ne: 'प्रविधि र डाटा' },
    { id: 'event', en: 'Event Coordination', ne: 'कार्यक्रम समन्वय' },
    { id: 'ground', en: 'On-Ground Mobilization', ne: 'स्थलगत परिचालन' },
    { id: 'research', en: 'Policy Research', ne: 'नीति अनुसन्धान' },
    { id: 'comm', en: 'Communication/PR', ne: 'सञ्चार र सम्पर्क' },
    { id: 'other', en: 'Others', ne: 'अन्य' },
];

const VolunteerPage = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        location: '',
        occupation: '',
        availability: 'Volunteer',
        message: '',
        otherRole: ''
    });

    const labelStyle = "text-[11px] font-bold uppercase tracking-[0.2em] text-[#2D5A43]/60 mb-2 block";
    const inputStyle = "w-full bg-white/60 border border-[#13231F]/5 rounded-2xl px-6 py-4 text-[#13231F] placeholder:text-[#13231F]/20 focus:border-[#2D5A43]/30 focus:bg-white focus:shadow-sm outline-none transition-all duration-300 font-sans";

    const toggleRole = (roleEn) => {
        setSelectedRoles(prev =>
            prev.includes(roleEn) ? prev.filter(r => r !== roleEn) : [...prev, roleEn]
        );
    };

    const validate = () => {
        if (!formData.name || !formData.email || !formData.phone || !formData.occupation) {
            toast.error("Please fill in your personal and professional details.");
            return false;
        }
        if (selectedRoles.length === 0) {
            toast.error("Please select at least one area of interest.");
            return false;
        }
        return true;
    };

    // --- GOOGLE FORM SUBMISSION LOGIC ---
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validate()) return;
        setIsSubmitting(true);

        // URL from your provided link (ending in formResponse)
        const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSeBkYxzeWDx5XIKq2HnR0iBRDoIdsG1x4LC1oLN1WXHQN3jeQ/formResponse";

        const rolesString = selectedRoles.includes('Others')
            ? [...selectedRoles.filter(r => r !== 'Others'), `Other: ${formData.otherRole}`].join(', ')
            : selectedRoles.join(', ');

        // Mapping your state to the Google Form Entry IDs
        const formBody = new URLSearchParams();
        formBody.append('entry.577226955', formData.name);         // Name
        formBody.append('entry.1594764281', formData.phone);        // Phone (Corrected mapping)
        formBody.append('entry.1614149903', formData.email);        // Email (Corrected mapping)
        formBody.append('entry.1115223043', formData.location);     // Location
        formBody.append('entry.628598553', formData.occupation);    // Occupation
        formBody.append('entry.1425969737', rolesString);           // Roles/Interests
        formBody.append('entry.2115440581', formData.availability); // Part-time/Volunteer
        formBody.append('entry.1438618639', formData.message);      // Message/Background

        try {
            await fetch(GOOGLE_FORM_URL, {
                method: 'POST',
                mode: 'no-cors', // Essential for Google Forms to bypass CORS
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: formBody.toString()
            });

            setIsSubmitted(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F2ED] text-[#13231F]">
            <Toaster position="top-center" />
            <Navbar />

            <main className="max-w-7xl mx-auto px-8 lg:px-20 pt-48 pb-32">
                <AnimatePresence mode="wait">
                    {!isSubmitted ? (
                        <motion.div
                            key="form-view"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="grid lg:grid-cols-12 gap-20"
                        >
                            {/* LEFT SIDE */}
                            <div className="lg:col-span-5 space-y-12">
                                <div className="space-y-6">
                                    <div className="flex items-center gap-3">
                                        <span className="w-8 h-[1px] bg-[#2D5A43]"></span>
                                        <h2 className="font-sans font-black text-[10px] uppercase tracking-[0.3em] text-[#2D5A43]">Join the Movement</h2>
                                    </div>
                                    <h1 className="text-6xl md:text-7xl font-medium font-serif leading-[1.1] tracking-tight">
                                        Building a <br />
                                        <span className="italic font-light text-[#2D5A43]">Community</span>
                                    </h1>
                                    <p className="text-xl font-sans opacity-70 leading-relaxed max-w-md">
                                        Contribute your time and expertise to help build a grassroots agenda that the nation cannot ignore.
                                    </p>
                                </div>

                                <div className="space-y-6 pt-8 border-t border-[#13231F]/10">
                                    <div className="flex gap-5 items-center">
                                        <div className="bg-[#13231F] p-3 rounded-full text-[#F5F2ED]"><Users size={18} /></div>
                                        <div>
                                            <h4 className="font-bold font-sans uppercase text-xs tracking-widest">Community Focused</h4>
                                            <p className="text-sm opacity-50">Local problems, national solutions.</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-5 items-center">
                                        <div className="bg-[#13231F] p-3 rounded-full text-[#F5F2ED]"><Sparkles size={18} /></div>
                                        <div>
                                            <h4 className="font-bold font-sans uppercase text-xs tracking-widest">Direct Impact</h4>
                                            <p className="text-sm opacity-50">Shape the discourse for Nepal</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT SIDE: FORM */}
                            <div className="lg:col-span-7 space-y-12">
                                <div className="space-y-6">
                                    <h3 className="font-serif italic text-2xl">01. Personal Details</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className={labelStyle}>Full Name / पूरा नाम</label>
                                            <input placeholder="Full Name *" className={inputStyle} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                                        </div>
                                        <div className="space-y-1">
                                            <label className={labelStyle}>Contact / सम्पर्क</label>
                                            <input placeholder="Phone Number *" className={inputStyle} value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className={labelStyle}>Email / इमेल</label>
                                            <input placeholder="Email Address *" className={inputStyle} value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                                        </div>
                                        <div className="space-y-1">
                                            <label className={labelStyle}>Current City / शहर</label>
                                            <input placeholder="Current City *" className={inputStyle} value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <h3 className="font-serif italic text-2xl">02. Occupation & Interests</h3>
                                    <div className="space-y-1">
                                        <label className={labelStyle}>What do you do? / तपाईको पेशा</label>
                                        <input
                                            placeholder="e.g. Student, Engineer, Social Worker..."
                                            className={inputStyle}
                                            value={formData.occupation}
                                            onChange={e => setFormData({ ...formData, occupation: e.target.value })}
                                        />
                                    </div>

                                    <div className="space-y-4">
                                        <label className={labelStyle}>Areas of Interest / चासोको क्षेत्रहरू</label>
                                        <div className="flex flex-wrap gap-3">
                                            {ROLES.map(role => (
                                                <button
                                                    key={role.id}
                                                    type="button"
                                                    onClick={() => toggleRole(role.en)}
                                                    className={`px-6 py-4 rounded-2xl border transition-all duration-300 flex items-center gap-4 ${selectedRoles.includes(role.en)
                                                        ? 'bg-[#2D5A43] border-[#2D5A43] text-white shadow-lg shadow-[#2D5A43]/20'
                                                        : 'bg-white border-[#13231F]/5 text-[#13231F]/60 hover:border-[#2D5A43]/20'
                                                        }`}
                                                >
                                                    <div className="text-left space-y-1">
                                                        <p className="text-sm font-bold leading-tight">{role.en}</p>
                                                        <p className={`text-sm leading-tight font-medium ${selectedRoles.includes(role.en) ? 'text-white/80' : 'text-[#13231F]/40'}`}>
                                                            {role.ne}
                                                        </p>
                                                    </div>
                                                    {selectedRoles.includes(role.en) && (
                                                        <div className="bg-white/20 p-1 rounded-full">
                                                            <Check size={14} strokeWidth={3} />
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <AnimatePresence>
                                        {selectedRoles.includes('Others') && (
                                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="pt-2 overflow-hidden">
                                                <label className={labelStyle}>Please specify interest</label>
                                                <input placeholder="How would you like to help?" className={inputStyle} value={formData.otherRole} onChange={e => setFormData({ ...formData, otherRole: e.target.value })} />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                <div className="space-y-6">
                                    <h3 className="font-serif italic text-2xl">03. Join The Community</h3>
                                    <div className="space-y-1 mb-4">
                                        <label className={labelStyle}>Availability / उपलब्धता</label>
                                        <div className="flex gap-4">
                                            {['Volunteer', 'Part-time'].map(type => (
                                                <button
                                                    key={type}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, availability: type })}
                                                    className={`flex-1 py-4 rounded-2xl border font-bold text-xs uppercase tracking-widest transition-all ${formData.availability === type ? 'bg-[#13231F] text-white border-[#13231F]' : 'bg-white border-[#13231F]/5 text-[#13231F]/40 hover:text-[#13231F]'}`}
                                                >
                                                    {type}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-1">
                                        <label className={labelStyle}>Describe your background / थप जानकारी</label>
                                        <textarea
                                            placeholder="Tell us a bit about your professional background or why you want to join..."
                                            className={inputStyle + " h-32 resize-none"}
                                            value={formData.message}
                                            onChange={e => setFormData({ ...formData, message: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className="w-full bg-[#13231F] text-white py-6 rounded-2xl font-black text-[11px] uppercase tracking-[0.4em] hover:bg-[#2D5A43] transition-all flex justify-center items-center gap-4 active:scale-[0.98] disabled:opacity-70"
                                >
                                    {isSubmitting ? <Loader2 className="animate-spin" /> : <>Send Application <ArrowRight size={18} /></>}
                                </button>
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="success-view"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="max-w-xl mx-auto text-center py-20"
                        >
                            <CheckCircle2 size={80} className="mx-auto text-[#2D5A43] mb-8 stroke-[1px]" />
                            <h2 className="text-5xl font-serif italic mb-4">Welcome to the Community.</h2>
                            <p className="text-lg opacity-60 mb-10">We've received your application and will reach out to you shortly.</p>
                            <button onClick={() => window.location.href = '/'} className="font-sans font-bold uppercase text-[10px] tracking-widest border-b-2 border-[#13231F] pb-1">Return Home</button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* PARTNERS SECTION */}
                {/* <div className="mt-40 pt-20 border-t border-black/5">
                    <div className="text-center space-y-4 mb-16">
                        <p className={labelStyle}>Strategic Partners / सहकार्य</p>
                        <h3 className="text-4xl font-bold text-[#13231F] tracking-tight">Supporting the Vision</h3>
                        <p className="max-w-2xl mx-auto text-black/50 text-lg">
                            Collaborating with organizations dedicated to legal excellence and social impact.
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-10 md:gap-16">
                        <a href="https://robinlawandpolicy.com" target="_blank" rel="noopener noreferrer"
                            className="group relative flex flex-col p-10 bg-white border border-black/5 rounded-[2.5rem] hover:shadow-2xl hover:border-[#2D5A43]/20 transition-all duration-500 w-full md:w-[400px]">
                            <div className="h-32 w-full bg-[#13231F] rounded-2xl flex items-center justify-center mb-8 overflow-hidden transition-transform duration-500 group-hover:scale-[1.02]">
                                <img src={robinLogo} alt="Robin Law" className="max-h-20 max-w-[80%] object-contain grayscale brightness-200 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-500" />
                            </div>
                            <div className="space-y-4 text-center md:text-left">
                                <h4 className="text-xl font-bold text-[#13231F] group-hover:text-[#2D5A43] transition-colors">Robin Law & Policy Associates</h4>
                                <p className="text-[15px] leading-relaxed text-black/60">Specializing in policy research and strategic advocacy in Nepal.</p>
                                <div className="pt-2 flex items-center justify-center md:justify-start text-[#2D5A43] font-bold text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                    Visit Website <ArrowRight size={14} className="ml-2" />
                                </div>
                            </div>
                        </a>

                        <a href="https://kumaritrust.com" target="_blank" rel="noopener noreferrer"
                            className="group relative flex flex-col p-10 bg-white border border-black/5 rounded-[2.5rem] hover:shadow-2xl hover:border-[#2D5A43]/20 transition-all duration-500 w-full md:w-[400px]">
                            <div className="h-32 w-full bg-[#FAF9F6] rounded-2xl flex items-center justify-center mb-8 transition-transform duration-500 group-hover:scale-[1.02]">
                                <img src={kumariLogo} alt="Kumari Trust" className="max-h-20 max-w-[80%] object-contain grayscale group-hover:grayscale-0 transition-all duration-500" />
                            </div>
                            <div className="space-y-4 text-center md:text-left">
                                <h4 className="text-xl font-bold text-[#13231F] group-hover:text-[#2D5A43] transition-colors">Kumari Trust</h4>
                                <p className="text-[15px] leading-relaxed text-black/60">Dedicated to empowering communities through sustainable health and education.</p>
                                <div className="pt-2 flex items-center justify-center md:justify-start text-[#2D5A43] font-bold text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                    Visit Website <ArrowRight size={14} className="ml-2" />
                                </div>
                            </div>
                        </a>
                    </div>
                </div> */}
            </main>
        </div>
    );
};

export default VolunteerPage;